import React, { useEffect, useState } from 'react';
import { db, getCollectionName } from '../firebase';
import { doc, setDoc } from '../firestore-compat';
import { uploadArquivo } from '../arquivos';
import EditorRico, { limparHtml } from './EditorRico';

// Perfil do aluno no INEP (tabela inep_perfis, um registro por e-mail).
// Visível só para o próprio aluno e para os admins (o banco bloqueia os outros alunos).
// somenteLeitura: visão do admin pela lista de alunos.

const UFS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];
const REDES = [
  ['linkedin', 'LinkedIn', 'linkedin.com/in/seu-perfil', '💼'],
  ['site', 'Site / Portfólio', 'seusite.com', '🌐'],
  ['facebook', 'Facebook', 'facebook.com/seu-perfil', '📘'],
  ['x', 'X (Twitter)', 'x.com/seu-usuario', '✖️'],
  ['instagram', 'Instagram', 'instagram.com/seu-perfil', '📸'],
  ['youtube', 'YouTube', 'youtube.com/seucanal', '▶️'],
  ['tiktok', 'TikTok', 'tiktok.com/@seu-usuario', '🎵'],
];

export const slugEmail = (email) => (email || '').toLowerCase().replace(/[^a-z0-9]/gi, '_');
const soDigitos = s => String(s || '').replace(/\D/g, '');
const mascaraCpf = s => soDigitos(s).slice(0, 11).replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
const mascaraTel = s => {
  const d = soDigitos(s).slice(0, 11);
  return d.length > 10 ? d.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3') : d.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
};
const mascaraCep = s => soDigitos(s).slice(0, 8).replace(/(\d{5})(\d)/, '$1-$2');
const fmt = v => { if (!v) return '—'; const d = new Date(v?.toDate ? v.toDate() : v); return isNaN(d) ? '—' : d.toLocaleString('pt-BR'); };

function Campo({ rotulo, children, largura }) {
  return <div className="inep-pf-campo" style={largura ? { gridColumn: `span ${largura}` } : undefined}><label>{rotulo}</label>{children}</div>;
}

export default function InepPerfil({ email, nome, userDoc, perfil, carregado = true, turmasNomes = [], somenteLeitura = false }) {
  const [rasc, setRasc] = useState(null);
  const [salvando, setSalvando] = useState('');
  const [enviando, setEnviando] = useState('');
  const [cepMsg, setCepMsg] = useState('');

  // Primeira carga: copia o perfil todo. Depois, só atualiza foto e capa (para não
  // apagar o que a pessoa está digitando quando chega uma atualização do banco).
  useEffect(() => {
    if (!carregado) return;
    setRasc(r => (r ? { ...r, avatarUrl: perfil?.avatarUrl, capaUrl: perfil?.capaUrl } : { ...(perfil || {}) }));
  }, [perfil, carregado]);
  if (!rasc) return null;

  const set = (campo, valor) => setRasc(r => ({ ...r, [campo]: valor }));
  const setEnd = (campo, valor) => setRasc(r => ({ ...r, endereco: { moraNoBrasil: true, ...(r.endereco || {}), [campo]: valor } }));
  const setRede = (campo, valor) => setRasc(r => ({ ...r, contatos: { ...(r.contatos || {}), [campo]: valor } }));
  const end = { moraNoBrasil: true, ...(rasc.endereco || {}) };
  const ref = doc(db, getCollectionName('inep_perfis'), slugEmail(email));

  const salvar = async () => {
    setSalvando('Salvando…');
    try {
      await setDoc(ref, { ...rasc, email: (email || '').toLowerCase(), curriculo: limparHtml(rasc.curriculo || ''), atualizadoEm: new Date().toISOString() }, { merge: true });
      setSalvando('✓ Perfil salvo');
      setTimeout(() => setSalvando(''), 2500);
    } catch (e) {
      setSalvando('Erro ao salvar: ' + e.message);
    }
  };

  const enviarImagem = async (file, campo) => {
    if (!file) return;
    setEnviando(campo);
    try {
      const { url } = await uploadArquivo(file, campo === 'capaUrl' ? 'inep/perfis' : 'inep/avatares');
      await setDoc(ref, { [campo]: url, email: (email || '').toLowerCase() }, { merge: true });
    } catch (e) {
      alert('Erro ao enviar imagem: ' + e.message);
    } finally {
      setEnviando('');
    }
  };

  const buscarCep = async (valor) => {
    const cep = soDigitos(valor);
    if (cep.length !== 8) return;
    setCepMsg('Buscando CEP…');
    try {
      const r = await fetch(`https://viacep.com.br/ws/${cep}/json/`).then(x => x.json());
      if (r.erro) { setCepMsg('CEP não encontrado.'); return; }
      setRasc(rr => ({ ...rr, endereco: { ...(rr.endereco || {}), moraNoBrasil: true, cep: mascaraCep(cep), estado: r.uf, cidade: r.localidade, rua: r.logradouro || rr.endereco?.rua || '', bairro: r.bairro || rr.endereco?.bairro || '' } }));
      setCepMsg('');
    } catch (_) {
      setCepMsg('Não foi possível buscar o CEP agora — preencha à mão.');
    }
  };

  const ro = somenteLeitura;
  const iniciais = (nome || '?').split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="inep-pf">
      {/* Capa + foto */}
      <div className="inep-pf-capa" style={rasc.capaUrl ? { backgroundImage: `url(${rasc.capaUrl})` } : undefined}>
        <div className="inep-pf-foto-col">
          <div className="inep-pf-foto">{rasc.avatarUrl ? <img src={rasc.avatarUrl} alt={nome} /> : iniciais}</div>
          {!ro && (
            <label className="inep-pf-btn">
              {enviando === 'avatarUrl' ? 'Enviando…' : 'Alterar foto 📷'}
              <input type="file" accept="image/*" hidden disabled={!!enviando} onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; enviarImagem(f, 'avatarUrl'); }} />
            </label>
          )}
        </div>
        <div className="inep-pf-nome"><b>{rasc.nomeCompleto || nome}</b><span>{email}</span></div>
        {!ro && (
          <label className="inep-pf-btn inep-pf-capa-btn">
            {enviando === 'capaUrl' ? 'Enviando…' : 'Atualizar capa'}
            <input type="file" accept="image/*" hidden disabled={!!enviando} onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; enviarImagem(f, 'capaUrl'); }} />
          </label>
        )}
      </div>

      <h3>Detalhes da conta</h3>
      <div className="inep-card inep-pf-grid">
        <Campo rotulo="Data de registro"><input value={fmt(userDoc?.createdAt)} readOnly /></Campo>
        <Campo rotulo="Turmas"><input value={turmasNomes.join(', ') || '—'} readOnly /></Campo>
        {(userDoc?.tipos?.length > 0 || userDoc?.cooperativas?.length > 0) && (<>
          <Campo rotulo="Tipo de profissional"><input value={(userDoc.tipos || []).join(', ') || '—'} readOnly /></Campo>
          <Campo rotulo="Cooperativa"><input value={(userDoc.cooperativas || []).join(', ') || '—'} readOnly /></Campo>
        </>)}
      </div>

      <h3>Dados pessoais</h3>
      <div className="inep-card inep-pf-grid">
        <Campo rotulo="Nome completo *" largura={2}><input value={rasc.nomeCompleto ?? nome ?? ''} readOnly={ro} onChange={e => set('nomeCompleto', e.target.value)} placeholder="Como deve sair no certificado" /></Campo>
        <Campo rotulo="Data de nascimento"><input type="date" value={rasc.nascimento || ''} readOnly={ro} onChange={e => set('nascimento', e.target.value)} /></Campo>
        <Campo rotulo="Profissão"><input value={rasc.profissao ?? (userDoc?.tipos || [])[0] ?? ''} readOnly={ro} onChange={e => set('profissao', e.target.value)} /></Campo>
        <Campo rotulo="Telefone"><input value={rasc.telefone || ''} readOnly={ro} onChange={e => set('telefone', mascaraTel(e.target.value))} placeholder="(21) 99999-9999" /></Campo>
        <Campo rotulo="Documento (CPF)"><input value={rasc.documento || ''} readOnly={ro} onChange={e => set('documento', mascaraCpf(e.target.value))} placeholder="000.000.000-00" /></Campo>
        <Campo rotulo="Biografia" largura={3}><textarea rows={2} value={rasc.bio || ''} readOnly={ro} onChange={e => set('bio', e.target.value.slice(0, 500))} placeholder="Conte-nos sobre você..." /></Campo>
        <Campo rotulo="Currículo" largura={3}>
          {ro
            ? <div className="inep-editor-area inep-editor-ro" dangerouslySetInnerHTML={{ __html: limparHtml(rasc.curriculo || '') || '<span style="opacity:.6">—</span>' }} />
            : <EditorRico value={rasc.curriculo || ''} onChange={v => set('curriculo', v)} />}
        </Campo>
      </div>

      <h3>Endereço</h3>
      <div className="inep-card inep-pf-grid">
        <Campo rotulo="Mora no Brasil?" largura={3}>
          <label className="inep-pf-toggle">
            <input type="checkbox" checked={end.moraNoBrasil !== false} disabled={ro} onChange={e => setEnd('moraNoBrasil', e.target.checked)} />
            <span>{end.moraNoBrasil !== false ? 'Sim' : 'Não'}</span>
          </label>
        </Campo>
        {end.moraNoBrasil !== false ? (<>
          <Campo rotulo="CEP"><input value={end.cep || ''} readOnly={ro} onChange={e => { const v = mascaraCep(e.target.value); setEnd('cep', v); buscarCep(v); }} placeholder="00000-000" />{cepMsg && <small>{cepMsg}</small>}</Campo>
          <Campo rotulo="Estado">
            <select value={end.estado || ''} disabled={ro} onChange={e => setEnd('estado', e.target.value)}>
              <option value="">—</option>{UFS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </Campo>
          <Campo rotulo="Cidade"><input value={end.cidade || ''} readOnly={ro} onChange={e => setEnd('cidade', e.target.value)} /></Campo>
          <Campo rotulo="Rua / Avenida" largura={2}><input value={end.rua || ''} readOnly={ro} onChange={e => setEnd('rua', e.target.value)} /></Campo>
          <Campo rotulo="Número"><input value={end.numero || ''} readOnly={ro} onChange={e => setEnd('numero', e.target.value)} /></Campo>
          <Campo rotulo="Bairro"><input value={end.bairro || ''} readOnly={ro} onChange={e => setEnd('bairro', e.target.value)} /></Campo>
          <Campo rotulo="Complemento" largura={2}><input value={end.complemento || ''} readOnly={ro} onChange={e => setEnd('complemento', e.target.value)} placeholder="Apartamento, bloco, etc." /></Campo>
        </>) : (
          <Campo rotulo="Endereço (fora do Brasil)" largura={3}><textarea rows={2} value={end.exterior || ''} readOnly={ro} onChange={e => setEnd('exterior', e.target.value)} /></Campo>
        )}
      </div>

      <h3>Contatos</h3>
      <div className="inep-card inep-pf-grid">
        {REDES.map(([campo, rotulo, ph, ic]) => (
          <Campo key={campo} rotulo={rotulo}>
            <div className="inep-pf-rede"><span>{ic}</span><input value={rasc.contatos?.[campo] || ''} readOnly={ro} onChange={e => setRede(campo, e.target.value)} placeholder={ph} /></div>
          </Campo>
        ))}
      </div>

      {!ro && (
        <div className="inep-pf-salvar">
          {salvando && <span>{salvando}</span>}
          <button className="inep-p1" style={{ background: 'var(--blue)', color: '#fff', width: 'auto', padding: '11px 24px' }} onClick={salvar}>💾 Salvar perfil</button>
        </div>
      )}
    </div>
  );
}
